// Calendario mensual de asistencias del alumno, estilo Kydemy (pedido Carlos 2-jul):
// rejilla Lun–Dom con una chip por sesión (verde ✓ asistió, roja ✗ faltó, ámbar ↻
// reposición), contadores del mes y navegación ‹ mes ›. La lista cronológica se
// conserva como vista alterna en la ficha.
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Check, X, RefreshCw } from 'lucide-react';
import { MemberAttendance } from '../api';

const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/** Estilo de chip por estado (acepta nombres Kydemy y del pase de lista nuevo). */
function chipOf(status: string): { cls: string; Icon: typeof Check } | null {
  if (status === 'present' || status === 'attended') return { cls: 'bg-emerald-500 text-white', Icon: Check };
  if (status === 'absent') return { cls: 'bg-rose-500 text-white', Icon: X };
  if (status === 'makeup' || status === 'made_up') return { cls: 'bg-amber-500 text-white', Icon: RefreshCw };
  return null;
}

/** YYYY-MM-DD de un registro (mismo criterio slice(0,10) que el resto de la ficha). */
const dayKey = (d: string | null) => String(d || '').slice(0, 10);

export default function AttendanceCalendar({ rows }: { rows: MemberAttendance[] }) {
  const today = new Date();
  const [ym, setYm] = useState<{ y: number; m: number }>({ y: today.getFullYear(), m: today.getMonth() });

  const byDate = useMemo(() => {
    const m = new Map<string, MemberAttendance[]>();
    for (const r of rows) {
      const k = dayKey(r.date);
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    return m;
  }, [rows]);

  // Celdas del mes: arranca en el lunes de la semana del día 1 (huecos = mes vecino).
  const cells = useMemo(() => {
    const first = new Date(ym.y, ym.m, 1);
    const offset = (first.getDay() + 6) % 7; // lunes=0
    const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
    const total = Math.ceil((offset + daysInMonth) / 7) * 7;
    return Array.from({ length: total }, (_, i) => {
      const d = new Date(ym.y, ym.m, i - offset + 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return { d, key, inMonth: d.getMonth() === ym.m };
    });
  }, [ym]);

  // Contadores del MES mostrado (los tiles de arriba de la ficha son históricos).
  const monthStats = useMemo(() => {
    let asis = 0, falt = 0, repo = 0;
    for (const c of cells) {
      if (!c.inMonth) continue;
      for (const r of byDate.get(c.key) || []) {
        const s = r.status || '';
        if (s === 'present' || s === 'attended') asis++;
        else if (s === 'absent') falt++;
        else if (s === 'makeup' || s === 'made_up') repo++;
      }
    }
    return { asis, falt, repo, total: asis + falt + repo };
  }, [cells, byDate]);

  // Tamaño adaptativo: con máx. 1 sesión por día en el mes, el chip crece
  // (con pocas clases se apreciaba muy apenas — pedido Carlos 2-jul); con días
  // de varias clases se mantiene compacto para que quepan todas.
  const bigChips = useMemo(() => {
    let mx = 0;
    for (const c of cells) if (c.inMonth) mx = Math.max(mx, (byDate.get(c.key) || []).length);
    return mx <= 1;
  }, [cells, byDate]);

  const rawLabel = new Date(ym.y, ym.m, 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  const monthLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1); // "Julio de 2026"
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const nav = (delta: number) => setYm(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  return (
    <div>
      {/* Contadores del mes + navegación */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-600 dark:text-emerald-400">
            Asistió {monthStats.asis}/{monthStats.total}
          </span>
          <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-rose-600 dark:text-rose-400">
            Faltó {monthStats.falt}/{monthStats.total}
          </span>
          {monthStats.repo > 0 && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-600 dark:text-amber-400">
              Reposición {monthStats.repo}/{monthStats.total}
            </span>
          )}
          {/* % del mes que se está viendo (pedido de Karla 29-jul; el tile de arriba es el mes
              en curso, este sigue las flechas ‹ ›). Base = asistió + faltó, la reposición no cuenta. */}
          {monthStats.asis + monthStats.falt > 0 && (
            <span className="rounded-full bg-surface-2 px-2 py-0.5 text-content-2"
              title={`${monthStats.asis} de ${monthStats.asis + monthStats.falt} clases del mes`}>
              {Math.round((monthStats.asis / (monthStats.asis + monthStats.falt)) * 100)}% del mes
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => nav(-1)} aria-label="Mes anterior"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-content-2 transition hover:bg-surface-2 hover:text-content">
            <ChevronLeft size={15} />
          </button>
          <span className="min-w-[130px] text-center text-sm font-semibold text-content">{monthLabel}</span>
          <button onClick={() => nav(1)} aria-label="Mes siguiente"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-content-2 transition hover:bg-surface-2 hover:text-content">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Rejilla mensual (scroll horizontal en pantallas chicas) */}
      <div className="overflow-x-auto">
        <div className="min-w-[640px] overflow-hidden rounded-xl border border-line">
          <div className="grid grid-cols-7 border-b border-line bg-canvas">
            {DOW.map((d) => (
              <div key={d} className="px-1 py-1.5 text-center text-[11px] font-semibold text-content-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((c) => {
              const recs = c.inMonth ? byDate.get(c.key) || [] : [];
              const isToday = c.key === todayKey;
              return (
                <div key={c.key}
                  className={`${bigChips ? 'min-h-[80px]' : 'min-h-[72px]'} border-b border-r border-line/60 p-1 [&:nth-child(7n)]:border-r-0 ${
                    !c.inMonth ? 'bg-canvas' : isToday ? 'bg-accent/10' : ''}`}>
                  <div className={`mb-0.5 text-right text-[11px] ${
                    !c.inMonth ? 'text-content-3/50'
                    : isToday ? 'font-bold text-accent-ink' : 'text-content-3'}`}>
                    {c.d.getDate()}
                  </div>
                  <div className="space-y-0.5">
                    {recs.map((r) => {
                      const chip = chipOf(r.status || '');
                      if (!chip) return null;
                      const { cls, Icon } = chip;
                      return (
                        <div key={r.attendance_id} title={`${r.class_name || 'Clase'} — ${c.key}`}
                          className={`flex items-center font-semibold leading-tight ${cls} ${
                            bigChips ? 'gap-1.5 rounded-lg px-2 py-1.5 text-xs' : 'gap-1 rounded-md px-1.5 py-0.5 text-[10px]'}`}>
                          <Icon size={bigChips ? 13 : 10} className="shrink-0" />
                          <span className="truncate">{r.class_name || 'Clase'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
